/**
 * Willow & Dove — Collection Page JavaScript
 *
 * Handles:
 * - Filter form submission (server-side via URL params)
 * - Sort dropdown (changes ?sort_by= URL param)
 * - Mobile filter drawer open/close
 * - Filter section collapse/expand
 * - Price range input debounce
 *
 * NOTE: NO client-side card filtering. Shopify handles filtering server-side.
 * All filter interactions update the URL and trigger a page navigation.
 */

(function () {
  'use strict';

  /* ── Scroll Position Preservation ──────────────────────────────────── */
  // Restore scroll position after filter/sort navigation
  (function restoreScroll() {
    var savedY = sessionStorage.getItem('wd_filter_scroll');
    if (savedY !== null) {
      sessionStorage.removeItem('wd_filter_scroll');
      // Wait for grid to render, then restore
      requestAnimationFrame(function() {
        window.scrollTo(0, parseInt(savedY, 10));
      });
    }
  })();

  function saveScrollAndNavigate(url) {
    sessionStorage.setItem('wd_filter_scroll', window.scrollY.toString());
    window.location.href = url;
  }

  /* ── DOM References ────────────────────────────────────────────────── */

  const filterDrawer = document.getElementById('filterDrawer');
  const sidebarOverlay = document.getElementById('sidebarOverlay');
  const mobileFilterBtn = document.getElementById('mobileFilterBtn');
  const closeFilterBtn = document.getElementById('closeFilterDrawer');
  const filterForm = document.getElementById('collectionFilterForm');
  const sortDropdown = document.getElementById('sortDropdown');

  /* ── Mobile Filter Drawer ──────────────────────────────────────────── */

  function openFilterDrawer() {
    if (!filterDrawer || !sidebarOverlay) return;
    filterDrawer.classList.add('active');
    sidebarOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    if (mobileFilterBtn) mobileFilterBtn.setAttribute('aria-expanded', 'true');
  }

  function closeFilterDrawer() {
    if (!filterDrawer || !sidebarOverlay) return;
    filterDrawer.classList.remove('active');
    sidebarOverlay.classList.remove('active');
    document.body.style.overflow = '';
    if (mobileFilterBtn) mobileFilterBtn.setAttribute('aria-expanded', 'false');
  }

  if (mobileFilterBtn) {
    mobileFilterBtn.addEventListener('click', openFilterDrawer);
  }

  if (closeFilterBtn) {
    closeFilterBtn.addEventListener('click', closeFilterDrawer);
  }

  if (sidebarOverlay) {
    sidebarOverlay.addEventListener('click', closeFilterDrawer);
  }

  // Mobile "Show Results" button closes the drawer
  var mobileApplyBtn = document.getElementById('mobileApplyFilters');
  if (mobileApplyBtn) {
    mobileApplyBtn.addEventListener('click', closeFilterDrawer);
  }

  // Close drawer on Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeFilterDrawer();
      closeSortDropdown();
    }
  });

  /* ── Filter Section Collapse/Expand ────────────────────────────────── */

  var filterToggles = document.querySelectorAll('.filter-section__toggle');

  filterToggles.forEach(function (toggle) {
    toggle.addEventListener('click', function () {
      var section = toggle.closest('.filter-section');
      if (!section) return;
      var isCollapsed = section.classList.toggle('collapsed');
      toggle.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    });
  });

  /* ── Filter Form Submission (server-side) ──────────────────────────── */

  /**
   * Builds a URL from the current filter form state and navigates to it.
   * Shopify Storefront Filtering uses URL query parameters.
   */
  function submitFilters() {
    if (!filterForm) return;

    var formData = new FormData(filterForm);
    var params = new URLSearchParams();

    // Preserve the current sort_by parameter
    var currentUrl = new URL(window.location.href);
    var currentSort = currentUrl.searchParams.get('sort_by');
    if (currentSort) {
      params.set('sort_by', currentSort);
    }

    // Collect all checked filter values
    for (var pair of formData.entries()) {
      var key = pair[0];
      var value = pair[1];
      if (value !== '' && value !== null) {
        params.append(key, value);
      }
    }

    // Build the new URL (collection URL + params)
    var collectionPath = window.location.pathname;
    var newUrl = collectionPath;
    var paramString = params.toString();
    if (paramString) {
      newUrl += '?' + paramString;
    }

    saveScrollAndNavigate(newUrl);
  }

  // Listen for checkbox and color swatch changes
  if (filterForm) {
    filterForm.addEventListener('change', function (e) {
      var target = e.target;
      // For checkboxes and color swatches, submit immediately
      if (target.type === 'checkbox') {
        submitFilters();
      }
    });
  }

  /* ── Price Range Input (debounced) ─────────────────────────────────── */

  var priceDebounceTimer = null;
  var priceMinInput = document.getElementById('filter-price-min');
  var priceMaxInput = document.getElementById('filter-price-max');

  function handlePriceChange() {
    clearTimeout(priceDebounceTimer);
    priceDebounceTimer = setTimeout(function () {
      submitFilters();
    }, 800);
  }

  if (priceMinInput) {
    priceMinInput.addEventListener('input', handlePriceChange);
    priceMinInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearTimeout(priceDebounceTimer);
        submitFilters();
      }
    });
  }

  if (priceMaxInput) {
    priceMaxInput.addEventListener('input', handlePriceChange);
    priceMaxInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearTimeout(priceDebounceTimer);
        submitFilters();
      }
    });
  }

  /* ── Sort Dropdown ─────────────────────────────────────────────────── */

  function closeSortDropdown() {
    if (sortDropdown) {
      sortDropdown.classList.remove('open');
      var trigger = sortDropdown.querySelector('.sort-dropdown__trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
  }

  if (sortDropdown) {
    var sortTrigger = sortDropdown.querySelector('.sort-dropdown__trigger');
    var sortOptions = sortDropdown.querySelectorAll('.sort-dropdown__option');

    if (sortTrigger) {
      sortTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = sortDropdown.classList.toggle('open');
        sortTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    sortOptions.forEach(function (option) {
      option.addEventListener('click', function () {
        var sortValue = option.getAttribute('data-sort-value');
        if (!sortValue) return;

        // Update the URL with the new sort_by param
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortValue);
        saveScrollAndNavigate(url.toString());
      });
    });
  }

  // Close sort dropdown when clicking outside
  document.addEventListener('click', function (e) {
    if (sortDropdown && !sortDropdown.contains(e.target)) {
      closeSortDropdown();
    }
  });

  /* ── Mobile Sort Select (inside filter drawer) ─────────────────────── */

  var mobileSortSelect = document.getElementById('mobileSortSelect');
  if (mobileSortSelect) {
    mobileSortSelect.addEventListener('change', function () {
      var sortValue = mobileSortSelect.value;
      if (!sortValue) return;
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortValue);
      saveScrollAndNavigate(url.toString());
    });
  }

})();


/* WD native sort select handler (2026-07-22) */
(function(){
  var sel=document.getElementById('wdSortSelect');
  if(!sel) return;
  sel.addEventListener('change',function(){
    if(!sel.value) return;
    try{ sessionStorage.setItem('wd_filter_scroll', String(window.scrollY)); }catch(e){}
    var url=new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    window.location.href=url.toString();
  });
})();
